import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaBuilding,
  FaIndustry,
  FaTruck,
  FaStore,
  FaHospital,
  FaGraduationCap,
  FaSearch,
  FaFilter,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaEuroSign,
  FaStar,
  FaQuoteLeft,
  FaArrowRight,
  FaPhone,
  FaGlobe,
  FaChevronDown,
} from "react-icons/fa";
import "./ReferencesPage.css";
 
const ReferencesPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
 
  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
 
  // Dynamic calculation of years since company founding
  const yearsInBusiness = new Date().getFullYear() - 1989;
 
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [selectedSolution, setSelectedSolution] = useState("all");
  const [showFilters, setShowFilters] = useState(true);
  const [industryExpanded, setIndustryExpanded] = useState(false);
  const [solutionExpanded, setSolutionExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 
  const industries = [
    { id: "all", name: "Všetky odvetvia", icon: FaBuilding },
    { id: "manufacturing", name: "Výroba", icon: FaIndustry },
    { id: "logistics", name: "Logistika", icon: FaTruck },
    { id: "retail", name: "Maloobchod", icon: FaStore },
    { id: "healthcare", name: "Zdravotníctvo", icon: FaHospital },
    { id: "education", name: "Vzdelávanie", icon: FaGraduationCap },
  ];
 
  const solutions = [
    { id: "all", name: "Všetky riešenia" },
    { id: "herkules", name: "Herkules" },
    { id: "spedicia", name: "Špedícia" },
    { id: "predajna", name: "Predajňa" },
    { id: "custom", name: "Custom riešenia" },
  ];
 
  // Get active filters for mobile tags
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (selectedIndustry !== "all") {
      const industry = industries.find(i => i.id === selectedIndustry);
      if (industry) {
        activeFilters.push({
          type: "industry",
          id: selectedIndustry,
          name: industry.name,
          onRemove: () => setSelectedIndustry("all")
        });
      }
    }
    
    if (selectedSolution !== "all") {
      const solution = solutions.find(s => s.id === selectedSolution);
      if (solution) {
        activeFilters.push({
          type: "solution",
          id: selectedSolution,
          name: solution.name,
          onRemove: () => setSelectedSolution("all")
        });
      }
    }
    
    return activeFilters;
  };
 
  const references = [
    {
      id: 1,
      company: "Quehenberger Logistics GmbH",
      industry: "logistics",
      solution: "spedicia",
      location: "Slovensko",
      employees: "100+",
      year: 2010,
      project: "Herkules 2.7 MULTI, sieť + modul SPED a ŠPEDÍCIA",
      description:
        "Jedna z najväčších implementácií čo sa týka objemu dokladov. Kompletný systém včítane analytického členenia, avšak bez modulu ODBYT. Riešenie je technicky prevádzkované v prostredí Microsoft Terminal Services a Citrix Metaframe.",
      testimonial:
        "Komplexný systém zvláda obrovský objem dokladov bez problémov. Špecializované moduly SPED a ŠPEDÍCIA sú presne to, čo potrebujeme.",
      author: "Vedenie spoločnosti",
      position: "Quehenberger Logistics GmbH",
      benefits: [
        "Najväčšia implementácia objemu dokladov",
        "Kompletné analytické členenie",
        "Špecializovaný modul SPED a ŠPEDÍCIA",
        "Microsoft Terminal Services a Citrix Metaframe",
      ],
      image: "/quehenberger.png",
      logo: "/quehenberger.png",
      rating: 5,
      link: "https://www.quehenberger.com/en",
    },
    {
      id: 2,
      company: "CROCODILLE ČR, spol. s r.o.",
      industry: "manufacturing",
      solution: "herkules",
      location: "Česká republika",
      employees: "100+",
      year: 2010,
      project: "Herkules 2.6, MULTI, sieť + vlastný IS BAGETY",
      description:
        "Kompletná implementácia software Herkules a vlastný informačný systém BAGETY pre sledovanie výroby, mobilný predaj a vyhodnocovanie. Systém BAGETY sa skladá z prevádzkovej časti a vyhodnocovacej časti, ktorá využíva SQL server firmy Microsoft.",
      testimonial:
        "Vlastný systém BAGETY v kombinácii s Herkules nám umožnil kompletne zautomatizovať výrobu a predaj.",
      author: "Vedenie spoločnosti",
      position: "CROCODILLE ČR",
      benefits: [
        "Kompletná implementácia Herkules",
        "Vlastný IS BAGETY pre výrobu",
        "Zákaznícke kiosky",
        "Erp riešenie",
        "Pokladničný system",
        "PDA",
        "NAVIS",
        "SQL server Microsoft integrácia",
      ],
      image: "/crocodille.webp",
      logo: "/crocodille.webp",
      rating: 5,
      link: "https://www.crocodille.com/",
    },
    {
      id: 3,
      company: "IMUNA PHARM a.s.",
      industry: "manufacturing",
      solution: "herkules",
      location: "Slovensko",
      employees: "100+",
      year: 2010,
      project: "HERKULES 3.0 WIN, sieť + moduly ODBYT, MTZ, VÝROBA liekov",
      description:
        "Veľká implementácia informačného systému pre výrobu a odbyt liekov. Okrem štandardných modulov firma používa aj moduly ODBYT, MTZ a VÝROBA.",
      testimonial:
        "Systém nám umožnil kompletne zautomatizovať výrobu liekov a ich odbyt v súlade s prísnou farmaceutickou legislatívou.",
      author: "Vedenie spoločnosti",
      position: "IMUNA PHARM a.s.",
      benefits: [
        "Veľká implementácia IS",
        "Moduly ODBYT, MTZ a VÝROBA",
        "Výroba a odbyt liekov",
        "Súlad s farmaceutickou legislatívou",
      ],
      image: "/imuna.jpg",
      logo: "/imuna.jpg",
      rating: 5,
      link: "https://www.imuna.sk/",
    },
    {
      id: 4,
      company: "KASA Záložná, a.s.",
      industry: "retail",
      solution: "custom",
      location: "Slovensko",
      employees: "50-100",
      year: 2010,
      project: "Herkules 2.6, MULTI + špecializovaný programový balík ZÁLOŽŇA",
      description:
        "Program Herkules je používaný ako centrálna zúčtovacia evidencia, kam sa prenášajú dáta z položiek a spracovávajú sa v module ZALO. Firma INVER pre túto spoločnosť vyvinula a udržiava špecializovaný programový balík ZÁLOŽŇA pre bežné vedenie pobočky záložne.",
      testimonial:
        "Špecializovaný programový balík ZÁLOŽŇA presne vyhovuje špecifikám našej činnosti.",
      author: "Vedenie spoločnosti",
      position: "KASA Záložná, a.s.",
      benefits: [
        "Centrálna zúčtovacia evidencia + Modul ZALO pre spracovanie dát",
        "Špecializovaný balík ZÁLOŽŇA",
        "Riadenie pobočiek záložne + Pokladničný displej",
      ],
      image: "/kasa.jpg",
      logo: "/kasa.jpg",
      rating: 5,
      link: "https://www.kasa.sk/",
    },
    {
      id: 5,
      company: "IQ SERVIS a.s.",
      industry: "retail",
      solution: "herkules",
      location: "Slovensko",
      employees: "do 10",
      year: 2010,
      project: "Herkules 2.6, MULTI + moduly SPLÁTKY a POKLADŇA",
      description:
        "Firma IQ SERVIS, a.s. má množstvo činností, rozprestretých medzi viacero spoločností. Ich záber činností je cez obchodné a finančné až k poskytovaniu služieb. Okrem štandardných modulov je v tejto firme nasadený aj modul SPLÁTKY a modul POKLADŇA (s možnosťou externého vedenia).",
      testimonial:
        "Moduly SPLÁTKY a POKLADŇA perfektne pokrývajú naše rozmanité obchodné aktivity.",
      author: "Vedenie spoločnosti",
      position: "IQ SERVIS a.s.",
      benefits: [
        "Viacero spoločností v systéme",
        "Obchodné a finančné služby",
        "Modul SPLÁTKY",
        "Modul POKLADŇA s externým vedením",
      ],
      image: "/iq_servis.jpg",
      logo: "/iq_servis.jpg",
      rating: 5,
      link: "https://www.iqservis.sk/",
    },
    {
      id: 6,
      company: "EGYM SLOVAKIA s.r.o.",
      industry: "retail",
      solution: "predajna",
      location: "Slovensko",
      employees: "do 10",
      year: 2010,
      project: "Herkules 2.6, MULTI + modul SATELIT + Custom MLM systém",
      description:
        "Informačný systém Herkules je v tejto firme nasadený v základnej podobe, s rozšírením o modul SATELIT, ktorý umožňuje spracovávať údaje dodané externým programom PREDAJŇA. Špecifikom zákazníka je systém na sledovanie MLM predaja a dealerov, vyhotovený na zákazku.",
      testimonial:
        "Custom MLM systém presne vyhovuje našej obchodnej stratégii a sledovaniu dealerov.",
      author: "Vedenie spoločnosti",
      position: "EGYM SLOVAKIA s.r.o.",
      benefits: [
        "Základná implementácia Herkules",
        "Modul SATELIT pre externé dáta",
        "Custom MLM systém na zákazku",
        "Sledovanie predaja a dealerov",
      ],
      image: "/egym.png",
      logo: "/egym.png",
      rating: 5,
      link: "",
    },
    {
      id: 7,
      company: "PIKARO TRADING, SE",
      industry: "retail",
      solution: "predajna",
      location: "Slovensko",
      employees: "",
      year: 2010,
      project:
        "Herkules 2.6 + osobitný obchodný systém s sledovaním plnenia objednávok a obchod so železnou rudou",
      description:
        "Pikaro má okrem účtovníctva aj osobitný obchodný systém s osobitým sledovaním priebehu plnenia objednávok + obchodný systém pre obchod so železnou rudou. Systém Herkules je rozšírený o špecializované moduly na zákazku pre špecifické potreby obchodovania s komoditami.",
      testimonial:
        "Osobitný obchodný systém s sledovaním plnenia objednávok a systém pre obchod so železnou rudou presne vyhovuje našej obchodnej stratégii.",
      author: "Vedenie spoločnosti",
      position: "PIKARO TRADING, SE",
      benefits: [
        "Základná implementácia Herkules účtovníctva",
        "Osobitný obchodný systém s sledovaním plnenia objednávok",
        "Špecializovaný systém pre obchod so železnou rudou",
        "Custom moduly na zákazku pre komodity",
      ],
      image: "/pikaro-logo-web-03.png",
      logo: "/pikaro-logo-web-03.png",
      rating: 5,
      link: "https://www.pikaro.sk/?lang=sk",
    },
    // {
    //   id: 7,
    //   company: "STRIP a.s.",
    //   industry: "manufacturing",
    //   solution: "herkules",
    //   location: "Slovensko",
    //   employees: "",
    //   year: 2010,
    //   project: "Herkules 2.6, sieť + špecializovaný modul ZSKL",
    //   description:
    //     "Firma STRIP a.s. je výrobná a obchodná firma, zaoberajúca sa výrobou a odbytom plechov. Okrem bežných modulov firma používa špecializovaný modul ZSKL (zrkadlové sklady), vyvinutý na zákazku firmou INVER. Pomocou nich sa vedie špeciálna skladová evidencia.",
    //   testimonial:
    //     "Špecializovaný modul ZSKL (zrkadlové sklady) presne vyhovuje našej výrobe plechov.",
    //   author: "Vedenie spoločnosti",
    //   position: "STRIP a.s.",
    //   benefits: [
    //     "Výroba a odbyt plechov",
    //     "Špecializovaný modul ZSKL",
    //     "Zrkadlové sklady na zákazku",
    //     "Špeciálna skladová evidencia",
    //   ],
    //   image: "/strip.jpg",
    //   logo: "/strip.jpg",
    //   rating: 5,
    // },
  ];
 
  // Filter logic
  const filteredReferences = references.filter((reference) => {
    const matchesSearch =
      reference.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reference.project.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry =
      selectedIndustry === "all" || reference.industry === selectedIndustry;
    const matchesSolution =
      selectedSolution === "all" || reference.solution === selectedSolution;
 
    return matchesSearch && matchesIndustry && matchesSolution;
  });
 
  const stats = [
    { value: "500+", label: "Spokojných klientov" },
    { value: `${yearsInBusiness}+`, label: "Rokov na trhu" },
    { value: "SK+ČR", label: "Slovensko a Česká republika" },
    { value: "24h", label: "Servisná odozva" },
  ];
 
  return (
    <div className="references-page">
      {/* Hero Section */}
      <section className="references-hero">
        <h1>Naše referencie</h1>
        <div className="hero-stats">
          {stats.map((stat, index) => (
            <div key={index} className="hero-stat">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>
 
      {/* References Introduction Section */}
      <section className="references-intro">
        <div className="container">
          <div className="intro-content">
            <h2>Úspešné projekty spokojných klientov</h2>
            <p>
              Už viac ako {yearsInBusiness} rokov pomáhame firmám po celom
              Slovensku digitalizovať ich procesy. Pozrite si naše úspešné
              projekty a spokojných klientov, ktorí dôverujú našim riešeniam pre
              efektívnejšie podnikanie.
            </p>
          </div>
        </div>
      </section>
 
      {/* Filters */}
      {showFilters && (
        <section className="filters-section">
          <div className="container">
            {/* Active Filters Tags - Mobile Only */}
            {isMobile && getActiveFilters().length > 0 && (
              <div className="active-filters">
                {getActiveFilters().map((filter) => (
                  <div key={`${filter.type}-${filter.id}`} className="active-filter-tag">
                    {filter.name}
                    <button 
                      className="remove-tag"
                      onClick={filter.onRemove}
                      title="Odstrániť filter"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="filters">
              <div className={`filter-group ${industryExpanded ? 'expanded' : ''}`}>
                <label onClick={() => setIndustryExpanded(!industryExpanded)}>
                  Odvetvie:
                </label>
                <div className="filter-buttons">
                  {industries.map((industry) => (
                    <button
                      key={industry.id}
                      className={`filter-btn ${
                        selectedIndustry === industry.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedIndustry(industry.id)}
                    >
                      <industry.icon />
                      {industry.name}
                    </button>
                  ))}
                </div>
              </div>
 
              <div className={`filter-group ${solutionExpanded ? 'expanded' : ''}`}>
                <label onClick={() => setSolutionExpanded(!solutionExpanded)}>
                  Riešenie:
                </label>
                <div className="filter-buttons">
                  {solutions.map((solution) => (
                    <button
                      key={solution.id}
                      className={`filter-btn ${
                        selectedSolution === solution.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedSolution(solution.id)}
                    >
                      {solution.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
 
      {/* References Grid */}
      <section className="references-section">
        <div className="container">
          <div className="section-header">
            <h2>Úspešné projekty</h2>
            <p>Nájdených: {filteredReferences.length} referencií</p>
          </div>
 
          <div className="references-grid">
            {filteredReferences.map((reference) => (
              <div key={reference.id} className="reference-card">
                <div className="reference-header">
                  <div className="company-info">
                    <div className="company-logo">
                      <img src={reference.logo} alt={reference.company} />
                    </div>
                    <div className="company-details">
                      <h3>{reference.company}</h3>
                      <div className="company-meta">
                        <span className="location">
                          <FaMapMarkerAlt />
                          {reference.location}
                        </span>
                        <span className="year">
                          <FaCalendarAlt />
                          {reference.year}
                        </span>
                        <span className="employees">
                          <FaUsers />
                          {reference.employees} zamestnancov
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="project-rating">
                    <div className="rating">
                      <FaStar />
                      <span>{reference.rating}</span>
                    </div>
                  </div>
                </div>
 
                <div className="reference-body">
                  <div className="project-info">
                    <h4>{reference.project}</h4>
                    <p>{reference.description}</p>
                  </div>
 
                  <div className="benefits-section">
                    <h5>Dosiahnuté výsledky:</h5>
                    <ul className="benefits-list">
                      {reference.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
 
                  <div className="testimonial">
                    {/* <FaQuoteLeft className="quote-icon" /> */}
                    <p>"{reference.testimonial}"</p>
                    <div className="testimonial-author">
                      <strong>{reference.author}</strong>
                      <span>{reference.position}</span>
                    </div>
                  </div>
                </div>
 
                <div className="reference-footer">
                  <div className="project-details">
                    <span className="industry-tag">
                      {
                        industries.find((i) => i.id === reference.industry)
                          ?.name
                      }
                    </span>
                    <span className="solution-tag">
                      {reference.solution.charAt(0).toUpperCase() +
                        reference.solution.slice(1)}
                    </span>
                  </div>
                  {reference.link !== "" && reference.link !== undefined && (
                    <button
                      onClick={() => {
                        if (
                          reference.link !== "" ||
                          reference.link !== undefined
                        ) {
                          window.open(reference.link, "_blank");
                        }
                      }}
                      className="btn btn-primary"
                    >
                      Stránka klienta
                      <FaArrowRight />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
 
      {/* Other Clients Section */}
      <section className="other-clients-section">
        <div className="container">
          <div className="section-header">
            <h2>Ďalší klienti používajúci náš softvér</h2>
            <p>Dôverujú nám aj tieto významné spoločnosti</p>
          </div>
 
          <div className="clients-grid">
            <div className="client-item">
              <h4>Valcovňa a.s, Košice</h4>
              <p>Obchod a výroba</p>
            </div>
            <div className="client-item">
              <h4>ECO-Invest a.s., Košice</h4>
              <p>Investície</p>
            </div>
            <div className="client-item">
              <h4>1. Východočeská potravinářská společnost</h4>
              <p>Žíželice, ČR, výroba a obchod</p>
            </div>
            <div className="client-item">
              <h4>AUTO PALAZZO Košice</h4>
              <p>Predaj áut a servis</p>
            </div>
            <div className="client-item">
              <h4>Technoinvest spol. s r.o.</h4>
              <p>Obchod</p>
            </div>
            <div className="client-item">
              <h4>Ferrorobot s.r.o.</h4>
              <p>Hoteliérstvo a obchod</p>
            </div>
            <div className="client-item">
              <h4>Metec s.r.o.</h4>
              <p>Obchod</p>
            </div>
            <div className="client-item">
              <h4>METAL TRADE COMAX, a.s.</h4>
              <p>Hutníctvo</p>
            </div>
            <div className="client-item">
              <h4>HB CONSULT, spol. s r.o</h4>
              <p>Účtovnícke a audítorské činnosti</p>
            </div>
            <div className="client-item">
              <h4>Spoločnosť REPUBLIKA VÝCHODU, s. r. o.</h4>
              <p>Služby pohostinstiev</p>
            </div>
            <div className="client-item">
              <h4>Mondieu</h4>
              <p>Kaviarne</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
 
export default ReferencesPage;